import { Box, Container, Stack, useMediaQuery } from "@mui/material";
import ManagementsHeader from "./ManagementsHeader/ManagementsHeader";
import { useCallback, useContext, useEffect, useState } from "react";
import "./Managements.scss";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import ManagementsLast from "./ManagementsLast/ManagementsLast";
import ManagementsGeneralList from "./ManagementsGeneralList/ManagementsGeneralList";
import ManagementsHistoric from "./ManagementsHistoric/ManagementsHistoric";
import {
  getManagements as getManagementsFetch,
  getMasterFilters,
} from "../../core/services/managements/managements.service";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { OwnerDataCups } from "../../core/models/ownerData/ownerData.model";
import {
  FormattedManagementsList,
  GetManagementsRequestBody,
  GetMasterFiltersRequestResponseSubtypes,
  GetMasterFiltersRequestResponseCups,
  HistoricFilters,
  MasterFiltersManagementCups,
  ManagementCodeText,
} from "../../core/models/managements/managements.model";
import { formatDateString } from "../../utils/dateUtils/formatDate.util";
import { formatManagementsList } from "../../utils/components/managements/managements.util";
import ManagementHistoricFilters from "./ManagementsHistoric/ManagementHistoricFilters/ManagementHistoricFilters";
import { FiltersChipsBills } from "../../core/models/bills/bills.model";
import DrawerFilters, {
  DeleteAllFiltersButton,
  DrawerFiltersButton,
  DrawerFiltersChips,
} from "../DrawerFilters/DrawerFilters";
import { isEmpty, uniq } from "lodash";
import { AGENT_MANAGEMENT_SERVICE_CONSTANTS } from "../../core/constants/agentManagements.constants";
import { RECIPIENT_IDS_LABEL } from "../../core/constants/constants";
import { getEntitySelectionFromLocalStorage } from "../../core/services/localStorage/localStorage.service";

const Managements = () => {
  const initialHistoricFilters = {
    cups: [],
    socialReason: [],
    type: [],
  };
  const { t } = useTranslation();

  const {
    ownerData,
    userData,
    setManagementsDetailHeader,
    selectedEntityList,
    isEntitySelected,
  } = useContext(TotalEnergiesContext);

  const isDesktopViewport = useMediaQuery("(min-width:1440px)");
  const [selectedTab, setSelectedTab] = useState<string>("managements");
  const [managementsLastList, setManagementsLastList] = useState<
    FormattedManagementsList[]
  >([]);
  const [managementsHistoricList, setManagementsHistoricList] = useState<
    FormattedManagementsList[]
  >([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRegistersQuery, setTotalRegistersQuery] = useState<number>(0);
  const [isHistoricTableLoading, setIsHistoricTableLoading] =
    useState<boolean>(false);
  const [isLastTableLoading, setIsLastTableLoading] = useState<boolean>(false);
  const [isFirstLoad, setIsFirstLoad] = useState<boolean>(true);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const [masterFiltersSubtypes, setMasterFiltersSubtypes] = useState<number[]>(
    [],
  );
  const [masterFiltersCups, setMasterFiltersCups] = useState<
    MasterFiltersManagementCups[]
  >([]);

  const [filtersHistoric, setFiltersHistoric] = useState<HistoricFilters>(
    initialHistoricFilters,
  );
  const [historicSearchParams, setHistoricSearchParams] =
    useState<HistoricFilters>(filtersHistoric);

  const [filtersHistoricChips, setFiltersHistoricChips] = useState<
    FiltersChipsBills[]
  >([]);

  const [pendingRequest, setPendingRequest] = useState<number>(0);
  const showNoResults =
    !isHistoricTableLoading && !totalRegistersQuery && !pendingRequest;

  const subTypesAgents: ManagementCodeText[] = Object.values(
    AGENT_MANAGEMENT_SERVICE_CONSTANTS,
  ).map((item) => {
    const subTypeElectricity =
      item.servicePayloadData.ELECTRICITY.subTypeReclamationId;
    const subTypeGas = item.servicePayloadData.GAS.subTypeReclamationId;

    const idSubtipoReclamacion = [subTypeElectricity, subTypeGas].filter(
      (id) => id,
    );

    return {
      id: idSubtipoReclamacion,
      label: item.titleKey,
    };
  });

  const subTypesClients: ManagementCodeText[] = RECIPIENT_IDS_LABEL.map(
    (item) => {
      return {
        id: [item.idSubtipoReclamacion],
        label: item.label,
      };
    },
  ).reduce((acc: ManagementCodeText[], curr: ManagementCodeText) => {
    const index = acc.findIndex((item) => item.label === curr.label);
    if (index >= 0) {
      acc[index].id.push(curr.id[0]);
    } else {
      acc.push(curr);
    }

    return acc;
  }, []);

  const allSubTypes = subTypesClients.concat(subTypesAgents);

  const getFiltersChips = useCallback((filtersHistoric: HistoricFilters) => {
    setFiltersHistoricChips([]);

    for (const [key, values] of Object.entries(filtersHistoric)) {
      const flatValues = Array.isArray(values) ? values.flat() : values;

      let label: string;
      let totalValues = 0;

      if (key === "type") {
        const typeLabel = allSubTypes.find((type) =>
          type.id.some((id) => id === flatValues[0]),
        );
        label = typeLabel?.label ?? "";

        totalValues = 0;

        for (let item of allSubTypes) {
          if (item.id.some((id) => flatValues.includes(id))) {
            totalValues++;
          }
        }
      } else {
        label = flatValues[0];
        totalValues = flatValues.length;
      }

      if (
        Array.isArray(flatValues) &&
        flatValues.length > 0 &&
        flatValues.some((val) => val)
      ) {
        setFiltersHistoricChips((prevFiltersChips) => [
          ...prevFiltersChips,
          {
            filterBillsKey: key as keyof HistoricFilters,
            totalValues: totalValues,
            label: typeof label === "string" ? label : String(label),
          },
        ]);
      }
    }
  }, []);

  useEffect(() => {
    const historicAbortController = new AbortController();

    if (ownerData && userData) {
      window.scrollTo({ top: 0, behavior: "auto" });
      getFiltersChips(filtersHistoric);

      return () => {
        historicAbortController.abort();
      };
    }
  }, [
    filtersHistoric,
    getFiltersChips,
    ownerData,
    page,
    rowsPerPage,
    userData,
  ]);

  const handleDeleteChip = (filterHistoricKey: string) => {
    setFiltersHistoricChips((prevFiltersChips) =>
      prevFiltersChips.filter(
        ({ filterBillsKey: prevFilterBillsKey }) =>
          prevFilterBillsKey !== filterHistoricKey,
      ),
    );

    let newFilters = filtersHistoric;

    const newReturnFilters = {
      ...newFilters,
      [filterHistoricKey]:
        initialHistoricFilters[filterHistoricKey as keyof HistoricFilters],
    };

    setFiltersHistoric(newReturnFilters);

    setHistoricSearchParams(newReturnFilters);
  };

  const onDeleteChip = (filterHistoricKey: string) => {
    handleDeleteChip(filterHistoricKey);
    setPage(0);
  };

  const handleDeleteAll = () => {
    setFiltersHistoricChips([]);
    setFiltersHistoric(initialHistoricFilters);
    setHistoricSearchParams(initialHistoricFilters);
    setPage(0);
  };

  // Check if is isAgent
  const { isAgent } = useContext(TotalEnergiesContext);

  const getManagements = useCallback(
    async (
      token: string,
      cups: OwnerDataCups[],
      page: number,
      rowsPerPage: number,
      abortSignal: AbortSignal,
      filtersHistoric: HistoricFilters,
      isAgentParam: boolean | null,
      cifList?: string[],
    ) => {
      if (isAgentParam && (!cifList || isEmpty(cifList))) {
        throw new Error(
          "is case user is agent, cifList is required in getManagements",
        );
      }
      const dateTo = new Date(new Date().setDate(new Date().getDate() + 1));
      const dateFrom = new Date(new Date(1, 1, 2000));
      const cifs = isAgentParam
        ? cifList!
        : uniq(cups.filter(({ cif }) => cif.length).map(({ cif }) => cif));

      const IdSubTipoReclamacionData = [
        55, 56, 57, 58, 62, 77, 229, 158, 159, 160, 161, 162, 163, 186, 209,
        215, 219, 221, 223, 227, 228, 231, 233, 235, 237, 241, 265, 267, 63, 64,
        65, 66, 105, 129, 133, 135, 164, 165, 166, 167, 168, 174, 220, 222, 224,
        226, 230, 232, 234, 236, 238, 242, 244, 262, 268,
      ];

      const clientIdSubTipoReclamacion = [
        ...new Set(
          subTypesClients
            .flatMap((item) => item.id)
            .concat(IdSubTipoReclamacionData),
        ),
      ];

      const agentIdSubTipoReclamacion = Object.values(
        AGENT_MANAGEMENT_SERVICE_CONSTANTS,
      )
        .map((item) => {
          const subTypePower =
            item.servicePayloadData.ELECTRICITY.subTypeReclamationId;
          const subTypeGas = item.servicePayloadData.GAS.subTypeReclamationId;

          return [subTypePower, subTypeGas];
        })
        .flatMap((item) => item)
        .filter((id) => id);

      const allSubTypesData = [
        ...new Set(
          clientIdSubTipoReclamacion.concat(agentIdSubTipoReclamacion),
        ),
      ];

      setIsHistoricTableLoading(true);
      setIsLastTableLoading(true);

      const getManagementsRequestBody: GetManagementsRequestBody = {
        Ids: cifs,
        IdTipoRegistro: [1, 2, 3, 4],
        fechaDesde: formatDateString(dateFrom).replace(",", ""),
        fechaHasta: formatDateString(dateTo).replace(",", ""),
        pageSize: rowsPerPage.toString(),

        cups: filtersHistoric.cups,
        clientes: filtersHistoric.socialReason,
        pageIndex: page.toString(),
        IdSubTipoReclamacion: filtersHistoric.type.length
          ? filtersHistoric.type
          : isAgent
            ? allSubTypesData
            : clientIdSubTipoReclamacion,
      };
      getManagementsFetch(getManagementsRequestBody, token, abortSignal)
        .then(({ TotalCallCenters, CallCenters }) => {
          setManagementsHistoricList(formatManagementsList(CallCenters));
          setTotalRegistersQuery(TotalCallCenters.Total);
        })
        .catch((error) => {
          if (error) {
            console.error(error);
            setTotalRegistersQuery(0);
          }
        })
        .finally(() => {
          setPendingRequest((prevPendingRequest) => {
            const number = Math.max(0, prevPendingRequest - 1);
            const hasRemainingRequests = number > 0;

            setIsHistoricTableLoading(hasRemainingRequests);

            if (number === 0) {
              setIsLastTableLoading(false);
            }
            return number;
          });
        });
    },
    [],
  );

  const fetchMasterFilters = useCallback(
    async (token: string, abortSignal: AbortSignal) => {
      const masterFiltersRequestBody = {
        pageSize: 1000,
        pageIndex: 0,
        type: 2,
      };

      getMasterFilters(masterFiltersRequestBody, token, abortSignal)
        .then((response) => {
          const typedResponse =
            response as GetMasterFiltersRequestResponseSubtypes[];
          const idSubtypes = typedResponse.flatMap((item) =>
            item.TiposGestion.flatMap((type) =>
              type.SubTipos.map((subtype) => subtype.IdSubTipo),
            ),
          );
          const subtypesUnique = [...new Set(idSubtypes)];
          setMasterFiltersSubtypes(subtypesUnique);
        })
        .catch((error) => {
          if (error) {
            console.error(error);
          }
        });

      const storedEntities = getEntitySelectionFromLocalStorage();

      const masterFiltersRequestBodyAgent = {
        pageSize: 1000,
        pageIndex: 0,
        type: 1,
        identities: storedEntities?.selection,
      };

      getMasterFilters(
        isAgent
          ? masterFiltersRequestBodyAgent
          : {
              ...masterFiltersRequestBody,
              type: 1,
            },
        token,
        abortSignal,
      )
        .then((response) => {
          const typedResponse =
            response as GetMasterFiltersRequestResponseCups[];

          const cupsSocialReasons: MasterFiltersManagementCups[] =
            typedResponse.flatMap((item) => {
              const socialReasons = item.Contratos.map((contract) => ({
                codeCups: item.Cups,
                socialReason: contract.NombreCliente,
              }));

              return socialReasons;
            });

          const socialReasonsUnique: MasterFiltersManagementCups[] =
            cupsSocialReasons.filter(
              (value, index, self) =>
                index ===
                self.findIndex(
                  (item) =>
                    item.codeCups === value.codeCups &&
                    item.socialReason === value.socialReason,
                ),
            );

          setMasterFiltersCups(socialReasonsUnique);
        })
        .catch((error) => {
          if (error) {
            console.error(error);
          }
        });
    },
    [],
  );

  useEffect(() => {
    const getManagementsAbortController = new AbortController();
    if (userData && ownerData && !isAgent) {
      window.scrollTo({ top: 0, behavior: "auto" });
      setManagementsHistoricList([]);
      setManagementsLastList([]);
      setPendingRequest((prevPendingRequest) => prevPendingRequest + 1);
      getManagements(
        userData.token,
        ownerData.cups,
        page,
        rowsPerPage,
        getManagementsAbortController.signal,
        filtersHistoric,
        isAgent,
      );

      return () => getManagementsAbortController.abort();
    } else if (isAgent && isEntitySelected) {
      window.scrollTo({ top: 0, behavior: "auto" });
      setManagementsHistoricList([]);
      setManagementsLastList([]);
      setPendingRequest((prevPendingRequest) => prevPendingRequest + 1);
      getManagements(
        userData!.token,
        [],
        page,
        rowsPerPage,
        getManagementsAbortController.signal,
        filtersHistoric,
        isAgent,
        selectedEntityList,
      );
    }
    return () => {
      getManagementsAbortController.abort();
    };
  }, [
    getManagements,
    ownerData,
    page,
    rowsPerPage,
    userData,
    isAgent,
    selectedEntityList,
    isEntitySelected,
    filtersHistoric,
  ]);

  useEffect(() => {
    const getMasterFiltersAbortController = new AbortController();
    if (userData && ownerData) {
      fetchMasterFilters(
        userData.token,
        getMasterFiltersAbortController.signal,
      );
    } else if (isEntitySelected) {
      fetchMasterFilters(
        userData!.token,
        getMasterFiltersAbortController.signal,
      );
    }

    return () => {
      getMasterFiltersAbortController.abort();
    };
  }, [fetchMasterFilters, isEntitySelected, ownerData, userData]);

  useEffect(() => {
    if (managementsHistoricList.length > 0) {
      setManagementsLastList(managementsHistoricList.slice(0, 3));
    } else {
      setManagementsLastList([]);
    }
  }, [managementsHistoricList]);

  useEffect(() => {
    if (isFirstLoad && managementsHistoricList !== undefined) {
      setIsFirstLoad(false);
    }
  }, [isFirstLoad, managementsHistoricList]);

  return (
    <>
      <ManagementsHeader
        ownerData={ownerData}
        isDesktopViewport={isDesktopViewport}
        changeTab={(tab: string) => setSelectedTab(tab)}
      />
      <Container component="article">
        {selectedTab === "managements" && (
          <>
            <ManagementsLast
              managementsLastList={managementsLastList}
              isTableLoading={isLastTableLoading}
              setManagementsDetailHeader={setManagementsDetailHeader}
            />

            <ManagementsGeneralList />
            <Typography component="h3" variant="h3" paddingBottom="1rem">
              {t("Gestiones.¿Desea hacer alguna otra gestión?")}
            </Typography>
            <Typography
              component="p"
              variant="body2"
              sx={{ color: "grey.700" }}
              paddingBottom="1.5rem"
            >
              {t(
                `Gestiones.Si no encuentra la gestión en esta lista, deberá ponerse en contacto con ${
                  isAgent ? "su gestor" : "un asesor comercial"
                }`,
              )}
            </Typography>
            <Stack
              direction="column"
              gap={"0.75rem"}
              paddingBottom={{ xs: "3.5rem", md: "5rem" }}
            >
              <div className="subtitle brand-gray">
                <Typography variant="body1" className="brand-gray">
                  <span className="t-icon-mail brand-red icon" />
                  {t(`Footer.Email contacto${isAgent ? " agentes" : ""}`)}
                </Typography>
              </div>
              {!isAgent && (
                <div className="subtitle brand-gray">
                  <Typography variant="body1" className="brand-gray">
                    <span className="t-icon-phone brand-red icon" />
                    {t("Footer.Teléfono contacto")}
                  </Typography>
                </div>
              )}
            </Stack>
          </>
        )}
        {selectedTab === "historic" && (
          <>
            <Container
              maxWidth="xl"
              component="article"
              className="historic__table-filters-container MuiContainer__historic-filters"
            >
              <Box
                component="div"
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={3}
              >
                <Box component="div" flexBasis="100%">
                  <DrawerFiltersButton
                    setIsFiltersOpen={setIsFiltersOpen}
                    isDisabled={
                      !totalRegistersQuery && !!!filtersHistoricChips.length
                    }
                    totalFiltersChips={filtersHistoricChips.length}
                  />

                  <DrawerFiltersChips
                    filterChips={filtersHistoricChips}
                    onDelete={onDeleteChip}
                  />
                </Box>
                <DeleteAllFiltersButton
                  totalRegistersQuery={totalRegistersQuery}
                  totalFiltersApplied={filtersHistoricChips.length}
                  isTableLoading={isHistoricTableLoading}
                  showNoResults={showNoResults}
                  handleDeleteAll={handleDeleteAll}
                />
                <DrawerFilters
                  isFiltersOpen={isFiltersOpen}
                  onApplyFilters={() => {
                    setFiltersHistoric(historicSearchParams);
                    setPage(0);
                    setIsFiltersOpen(false);
                  }}
                  onClose={() => setIsFiltersOpen(false)}
                  onClearFilters={() => {
                    setHistoricSearchParams(initialHistoricFilters);
                  }}
                >
                  <ManagementHistoricFilters
                    allSubTypes={allSubTypes}
                    masterFiltersSubtypes={masterFiltersSubtypes}
                    masterFiltersCups={masterFiltersCups}
                    ownerDataCups={ownerData?.cups || []}
                    searchParamsSearchBar={historicSearchParams}
                    setSearchParamsSearchBar={setHistoricSearchParams}
                  />
                </DrawerFilters>
              </Box>
            </Container>
            <ManagementsHistoric
              managementsHistoricList={managementsHistoricList}
              totalRegistersQuery={totalRegistersQuery}
              isHistoricTableLoading={isHistoricTableLoading}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
              setManagementsDetailHeader={setManagementsDetailHeader}
              pendingRequest={pendingRequest}
              isTableFiltered={filtersHistoric !== initialHistoricFilters}
            />
          </>
        )}
      </Container>
    </>
  );
};

export default Managements;
